import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { GuestAwareTechDocsHome } from './components/techdocs/GuestAwareTechDocsHome';
import { GuestAwareReportIssue } from './components/techdocs/GuestAwareReportIssue';
import { GuestHideRepositoryLinks } from './components/techdocs/GuestHideRepositoryLinks';
import { renderGuestAwareTechDocsReaderPage } from './components/techdocs/GuestAwareTechDocsReaderPage';
import { AttachmentLinks } from './components/techdocs/AttachmentLinks';
import { WikiContentStyles } from './components/techdocs/WikiContentStyles';
import { MoveWikiPage } from './components/techdocs/MoveWikiPage';
import { ReaderPageBySection } from './components/techdocs/ReaderPageBySection';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { GuestAwareSettingsPage } from './components/settings/GuestAwareSettingsPage';
import { GuestProvider, useGuestState } from './components/guest';
import {
  FacetedEntityLifecyclePicker,
  FacetedEntityOwnerPicker,
  FacetedEntityTagPicker,
  FacetedEntityTypePicker,
} from './components/catalog/filters';
import { CatalogIndexFilters } from './components/catalog/filters/CatalogIndexFilters';
import { GroupsIndexFilters } from './components/catalog/filters/GroupsIndexFilters';
import { apis, guestFullAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import SignInWithLogo from './components/Root/SignInWithLogo';
// import {
//   AgreementDocAddonEmbedded,
//   AgreementDocAddonPage,
//   RemoveSidebarAddon,
// } from "@internal/plugin-agreement-doc-addon";
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
// import { keycloakAuthApiRef } from './apis';
import {
  AlertDisplay,
  Content,
  ContentHeader,
  PageWithHeader,
  OAuthRequestDialog,
  Progress,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import LightIcon from "@material-ui/icons/WbSunny";
import DarkIcon from "@material-ui/icons/NightsStay";
import { UnifiedThemeProvider } from "@backstage/theme";
import { LightTheme, DarkTheme } from "./theme";
// import { AgreementDocAddonPage } from '@internal/plugin-agreement-doc-addon';
import { GcsGlobalPage } from './components/gcsFiles/GlobalPage';
import CssBaseline from '@material-ui/core/CssBaseline'; 
import { HomePage } from './components/home/HomePage';
import { WikiPage } from './components/wiki/WikiPage';
import { WikiImportRoute } from './components/wikiImport/WikiImportRoute';
import { LinksPage } from './components/links/LinksPage';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  type DefaultEntityFilters,
  type EntityFilter,
  UserListPicker,
  getEntityRelations,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  RELATION_PART_OF,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';

const defaultThemeId = 'lightTheme';

const applyDefaultTheme = (themeId: string) => {
  try {
    if (!window.localStorage.getItem('theme')) {
      window.localStorage.setItem('theme', themeId);
    }
  } catch {
    return;
  }
};

applyDefaultTheme(defaultThemeId);

const app = createApp({
  apis,
  themes: [
    {
      id: defaultThemeId,
      title: "LIGHT THEME",
      variant: "light",
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={LightTheme.theme()} children={children} />
      ),
    },
    {
      id: "darkTheme",
      title: "DARK THEME",
      variant: "dark",
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={DarkTheme.theme()} children={children} />
      ),
    }
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const providers: any[] = [
        {
          id: 'azure-auth-provider',
          title: 'Azure',
          message: 'Sign in using Microsoft Azure',
          apiRef: microsoftAuthApiRef,
        },
        /*
        {
          id: 'oidc-provider',
          title: 'Keycloak',
          message: 'Sign in using Keycloak',
          apiRef: keycloakAuthApiRef,
        },
        */
      ];

      providers.push('guest');

      // Segundo acceso "guest" con permisos completos, solo para depurar en
      // local (ver packages/backend/src/authGuestFullProvider.ts). Se añade
      // junto a "guest" en la misma rejilla de proveedores de login.
      if (process.env.NODE_ENV === 'development') {
        providers.push({
          id: 'guest-full-auth-provider',
          title: 'Guest (full access)',
          message: 'Sign in as guest with full permissions (local debugging only)',
          apiRef: guestFullAuthApiRef,
        });
      }

      return (
        <SignInWithLogo
          {...props}
          auto
          providers={providers}
        />
      );
    },
  },
});

const RootRedirect = () => <Navigate to="home" />;

const guestApiExplorerColumns = [
  {
    ...CatalogTable.columns.createNameColumn({ defaultKind: 'api' }),
    width: '20%',
  },
  {
    ...CatalogTable.columns.createMetadataDescriptionColumn(),
    width: '30%',
  },
  {
    ...CatalogTable.columns.createSpecTypeColumn(),
    width: '14%',
  },
  {
    ...CatalogTable.columns.createSpecLifecycleColumn(),
    width: '14%',
  },
  {
    ...CatalogTable.columns.createTagsColumn(),
    width: '22%',
  },
];

const defaultApiExplorerColumns = [
  CatalogTable.columns.createTitleColumn({ hidden: true }),
  CatalogTable.columns.createNameColumn({ defaultKind: 'api' }),
  CatalogTable.columns.createSystemColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  CatalogTable.columns.createTagsColumn(),
];

type ApiEntityFilters = DefaultEntityFilters & {
  system?: EntityFilter;
};

const SystemQueryFilter = () => {
  const { queryParameters, updateFilters } = useEntityList<ApiEntityFilters>();
  const system = queryParameters.system;

  React.useEffect(() => {
    if (!system) {
      return;
    }

    const systems = (Array.isArray(system) ? system : [system]).filter(
      (value): value is string => typeof value === 'string',
    );

    updateFilters({
      system: {
        getCatalogFilters: () => ({
          'relations.partOf': systems,
        }),
        filterEntity: (entity: Entity) =>
          systems.some(systemRef =>
            getEntityRelations(entity, RELATION_PART_OF).some(
              relation => stringifyEntityRef(relation) === systemRef,
            ),
          ),
        toQueryValue: () => systems,
      },
    });
  }, [system, updateFilters]);

  return null;
};

const ApiExplorerWithSystemFilter = ({ isGuest }: { isGuest: boolean }) => (
  <PageWithHeader
    themeId="apis"
    title="APIs"
    subtitle="Explore the APIs available in the catalog"
  >
    <Content>
      <ContentHeader title="" />
      <EntityListProvider>
        <SystemQueryFilter />
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <EntityKindPicker initialFilter="api" hidden />
            <FacetedEntityTypePicker />
            <UserListPicker hidden={isGuest} />
            {!isGuest && <FacetedEntityOwnerPicker />}
            <FacetedEntityLifecyclePicker />
            <FacetedEntityTagPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <CatalogTable
              columns={isGuest ? guestApiExplorerColumns : defaultApiExplorerColumns}
              actions={isGuest ? [] : undefined}
            />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </EntityListProvider>
    </Content>
  </PageWithHeader>
);

const GuestAwareApiExplorerRoute = () => {
  const { isGuest, loading } = useGuestState();

  if (loading) {
    return <Progress />;
  }

  return <ApiExplorerWithSystemFilter isGuest={isGuest} />;
};

const routes = (
  <FlatRoutes>
    <Route
      path="/"
      element={<RootRedirect />}
    />

    <Route
      path="/home"
      element={
        <HomePage />
      }
    />

    <Route
      path="/catalog"
      element={<CatalogIndexPage filters={<CatalogIndexFilters />} />}
    />
    <Route
      path="/groups"
      element={<CatalogIndexPage filters={<GroupsIndexFilters />} />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/wiki" element={<WikiPage />} />
    <Route path="/wiki/import" element={<WikiImportRoute />} />
    <Route path="/links" element={<LinksPage />} />

    <Route path="/docs" element={<TechDocsIndexPage />}>
      <GuestAwareTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <ReaderPageBySection>
          <TechDocsReaderPage>
            {renderGuestAwareTechDocsReaderPage}
          </TechDocsReaderPage>
        </ReaderPageBySection>
      }
    >
      <TechDocsAddons>
        <GuestAwareReportIssue />
        <GuestHideRepositoryLinks />
        <WikiContentStyles />
        <AttachmentLinks />
        <MoveWikiPage />
        <Mermaid
          enableZoom
          zoomOptions={{
            scaleExtent: [0.1, 10],
            translateExtent: [[-1000, -1000], [1000, 1000]]
          }}
        />
      </TechDocsAddons>
    </Route>

    <Route
      path="/create"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <ScaffolderPage />
        </RequirePermission>
      }
    />

    <Route
      path="/api-docs"
      element={<GuestAwareApiExplorerRoute />}
    />

    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    <Route path="/settings" element={<GuestAwareSettingsPage />} />

    <Route path="/catalog-graph" element={<CatalogGraphPage />} />

    <Route
      path="/gcs-explorer"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <GcsGlobalPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <CssBaseline />
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <GuestProvider>
        <Root>{routes}</Root>
      </GuestProvider>
    </AppRouter>
  </>,
);
